import React, { useState, useMemo } from 'react'
import styled from '@emotion/styled'
import moment from 'moment-timezone'
import Modal from '@components/common/modal/Modal'
import type { DateMenus } from '../../../../types/groupOrder/dateMenus'
import { buildCalendarData, truncateChefs } from './menuGenerationUtils'

interface AcceptScheduleModalProps {
  previews: DateMenus[]
  onCreateMenus: (activeMap: Record<string, boolean>) => void
  onClose: () => void
  isCreating: boolean
  createProgress: { current: number; total: number; dateStr: string } | null
}

const AcceptScheduleModal: React.FC<AcceptScheduleModalProps> = ({
  previews,
  onCreateMenus,
  onClose,
  isCreating,
  createProgress,
}) => {
  // Track active state per date; default unchecked
  const [activeMap, setActiveMap] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {}
    for (const dm of previews) {
      map[dm.dateStr] = false
    }

    return map
  })

  const hasWeekends = useMemo(() => {
    return previews.some((p) => {
      const dow = new Date(p.dateStr + 'T00:00:00').getDay()

      return dow === 0 || dow === 6
    })
  }, [previews])

  const months = useMemo(
    () => buildCalendarData(previews, hasWeekends),
    [previews, hasWeekends],
  )

  const previewDates = useMemo(
    () => new Set(previews.map((p) => p.dateStr)),
    [previews],
  )

  const toggleActive = (dateStr: string) => {
    setActiveMap((prev) => ({ ...prev, [dateStr]: !prev[dateStr] }))
  }

  const toggleAll = () => {
    const allActive = Object.values(activeMap).every(Boolean)
    setActiveMap((prev) => {
      const next: Record<string, boolean> = {}
      for (const key of Object.keys(prev)) {
        next[key] = !allActive
      }

      return next
    })
  }

  const activeCount = Object.values(activeMap).filter(Boolean).length

  return (
    <Modal
      title="Review Date Menus"
      hideModal={onClose}
      width="950px"
      includePadding={false}
    >
      <ScrollableContent>
        <ToggleAllRow>
          <ToggleAllButton onClick={toggleAll}>
            {Object.values(activeMap).every(Boolean)
              ? 'Deselect All'
              : 'Select All'}
          </ToggleAllButton>
          <ActiveCount>
            {activeCount} of {previews.length} dates active
          </ActiveCount>
        </ToggleAllRow>

        {months.map((m) => (
          <MonthSection key={`${m.year}-${m.month}`}>
            <MonthLabel>{m.label}</MonthLabel>
            <CalendarGrid columns={hasWeekends ? 7 : 5}>
              <DayHeader>Mon</DayHeader>
              <DayHeader>Tue</DayHeader>
              <DayHeader>Wed</DayHeader>
              <DayHeader>Thu</DayHeader>
              <DayHeader>Fri</DayHeader>
              {hasWeekends && <DayHeader>Sat</DayHeader>}
              {hasWeekends && <DayHeader>Sun</DayHeader>}
              {m.weeks.map((week, wi) =>
                week.map((cell, ci) => {
                  if (!cell) {
                    return <EmptyCell key={`${wi}-${ci}`} />
                  }
                  const hasMenus = previewDates.has(cell.date)

                  return (
                    <Cell
                      key={cell.date}
                      hasMenus={hasMenus}
                      isActive={!!activeMap[cell.date]}
                    >
                      <DayNumber>{cell.dayOfMonth}</DayNumber>
                      {hasMenus && (
                        <>
                          <TimeInfo>
                            Dropoff: {moment(cell.dropoff).format('h:mma')}
                          </TimeInfo>
                          <TimeInfo>
                            Cutoff: {moment(cell.cutoff).format('MMM D h:mma')}
                          </TimeInfo>
                          <CellDivider />
                          <MenuCount>
                            {cell.menuCount} menu
                            {cell.menuCount !== 1 ? 's' : ''}
                          </MenuCount>
                          <ChefNames>{truncateChefs(cell.chefNames)}</ChefNames>
                          <CheckboxRow>
                            <input
                              type="checkbox"
                              checked={!!activeMap[cell.date]}
                              onChange={() => toggleActive(cell.date)}
                            />
                            <CheckboxLabel>Active</CheckboxLabel>
                          </CheckboxRow>
                        </>
                      )}
                    </Cell>
                  )
                }),
              )}
            </CalendarGrid>
          </MonthSection>
        ))}
      </ScrollableContent>

      <Footer>
        <CancelButton onClick={onClose} disabled={isCreating}>
          Back
        </CancelButton>
        <CreateButton
          onClick={() => onCreateMenus(activeMap)}
          disabled={isCreating}
        >
          {isCreating && createProgress
            ? `Creating ${createProgress.current} of ${createProgress.total}...`
            : isCreating
              ? 'Creating...'
              : 'Create Menus'}
        </CreateButton>
      </Footer>
    </Modal>
  )
}

const ScrollableContent = styled.div`
  padding: 16px 20px 0;
  max-height: 65vh;
  overflow-y: auto;
`

const ToggleAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

const ToggleAllButton = styled.button`
  background: none;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  color: #555;

  &:hover {
    background: #f5f5f5;
  }
`

const ActiveCount = styled.span`
  font-size: 12px;
  color: #888;
`

const MonthSection = styled.div`
  margin-bottom: 24px;
`

const MonthLabel = styled.h4`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
`

const CalendarGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: 1px;
  background: #bbb;
  border: 1px solid #bbb;
  border-radius: 4px;
  overflow: hidden;
`

const DayHeader = styled.div`
  background: #f5f5f5;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  padding: 6px 0;
`

const EmptyCell = styled.div`
  background: #fafafa;
  min-height: 80px;
  min-width: 0;
`

const Cell = styled.div<{ hasMenus: boolean; isActive: boolean }>`
  background: ${(props) =>
    props.isActive ? '#d9f0f4' : props.hasMenus ? '#f0f9fb' : '#fff'};
  min-height: 80px;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

const DayNumber = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
`

const TimeInfo = styled.span`
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CellDivider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 3px 0;
`

const MenuCount = styled.span`
  font-size: 10px;
  color: #555;
`

const ChefNames = styled.span`
  font-size: 10px;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: auto;
  padding-top: 4px;
  cursor: pointer;

  input {
    margin: 0;
    cursor: pointer;
  }
`

const CheckboxLabel = styled.span`
  font-size: 10px;
  color: #555;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
`

const CancelButton = styled.button`
  background: #fff;
  border: 1px solid #d0d0d0;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  color: #555;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CreateButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export default AcceptScheduleModal
